import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSport } from '@/hooks/useSport'
import { ScoreCard } from '@/components/ScoreCard'
import { Skeleton } from '@/components/ui/Skeleton'
import { FilterButtonGroup } from '@/components/ui/FilterButtonGroup'
import { WeekNavigator } from '@/components/ui/WeekNavigator'
import { SeasonSelector } from '@/components/ui/SeasonSelector'
import { usePolling } from '@/hooks/usePolling'
import {
  getWeekStart, getWeekEnd, shiftWeeks, toIsoDate,
  getSeasonOptions, getCurrentSeason, isCurrentWeek,
} from '@/lib/dateUtils'
import type { SeasonOption } from '@/lib/dateUtils'
import clsx from 'clsx'

import type { GameRow } from '@/types/api'

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Live', value: 'in_progress' },
  { label: 'Final', value: 'final' },
  { label: 'Scheduled', value: 'scheduled' },
]

const SEASON_OPTIONS = getSeasonOptions(5)

interface PageState {
  start: number
  length: number
  totalRecords: number
  data: GameRow[]
  loading: boolean
}

export default function ScoresPage() {
  const { sport, sportLabel } = useSport()
  const [rankedOnly, setRankedOnly] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()))
  const [selectedDay, setSelectedDay] = useState<string | null>(null) // ISO date; null = whole week
  const [dayCounts, setDayCounts] = useState<Record<string, number>>({})
  const [selectedSeason, setSelectedSeason] = useState<SeasonOption>(() => getCurrentSeason())
  const pageLength = 24
  const [page, setPage] = useState<PageState>({
    start: 0, length: pageLength, totalRecords: 0, data: [], loading: true,
  })

  const weekEnd = useMemo(() => getWeekEnd(weekStart), [weekStart])

  // The 7 days of the current week (for the day-filter buttons).
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart)
      d.setDate(d.getDate() + i)
      return d
    }),
    [weekStart],
  )

  // Games-per-day for the week, so days with none can be grayed out.
  useEffect(() => {
    fetch(`/api/v1/${sport}/games/day_counts?from=${toIsoDate(weekStart)}&to=${toIsoDate(weekEnd)}`, {
      headers: { Accept: 'application/json' },
    })
      .then(r => r.json())
      .then(d => setDayCounts(d && typeof d === 'object' ? d : {}))
      .catch(() => setDayCounts({}))
  }, [sport, weekStart, weekEnd])

  const fetchPage = useCallback(async (start: number) => {
    setPage(p => ({ ...p, loading: true }))
    const params = new URLSearchParams({
      draw: '1',
      start: String(start),
      length: String(pageLength),
      'order[0][column]': '0',
      'order[0][dir]': 'desc',
      ranked_only: String(rankedOnly),
      // DataTables column search for date_range on starts_at (column index 0)
      'columns[0][data]': 'starts_at',
      'columns[0][searchable]': 'true',
      'columns[0][search][value]': selectedDay
        ? `${selectedDay}~${selectedDay}`
        : `${toIsoDate(weekStart)}~${toIsoDate(weekEnd)}`,
      'columns[0][search][regex]': 'false',
    })
    if (statusFilter !== 'all') params.set('status', statusFilter)

    try {
      const res = await fetch(`/api/v1/${sport}/datatables/games.json?${params}`, {
        headers: { Accept: 'application/json' },
      })
      const json = await res.json()
      setPage({
        start,
        length: pageLength,
        totalRecords: json.recordsFiltered || json.recordsTotal || 0,
        data: json.data || [],
        loading: false,
      })
    } catch {
      setPage(p => ({ ...p, loading: false }))
    }
  }, [sport, rankedOnly, statusFilter, weekStart, weekEnd, selectedDay])

  useEffect(() => { fetchPage(0) }, [fetchPage])

  const hasLiveGames = useMemo(() =>
    page.data.some(g => {
      const s = g.status?.toLowerCase()
      return s === 'live' || s === 'in_progress'
    }), [page.data])

  // Only poll for live updates when viewing the current week
  usePolling(() => fetchPage(page.start), 30_000, hasLiveGames && isCurrentWeek(weekStart))

  const totalPages = useMemo(() => Math.ceil(page.totalRecords / page.length), [page.totalRecords, page.length])
  const currentPage = useMemo(() => Math.floor(page.start / page.length) + 1, [page.start, page.length])

  const handlePrevWeek = useCallback(() => {
    setSelectedDay(null)
    setWeekStart(prev => getWeekStart(shiftWeeks(prev, -1)))
  }, [])

  const handleNextWeek = useCallback(() => {
    setSelectedDay(null)
    setWeekStart(prev => {
      const next = getWeekStart(shiftWeeks(prev, 1))
      const now = getWeekStart(new Date())
      return next.getTime() > now.getTime() ? now : next
    })
  }, [])

  const handleToday = useCallback(() => {
    setSelectedDay(null)
    setWeekStart(getWeekStart(new Date()))
    setSelectedSeason(getCurrentSeason())
  }, [])

  const handleSeasonChange = useCallback((season: SeasonOption) => {
    setSelectedSeason(season)
    // Jump to the last week of that season, or current week if it's the current season
    const current = getCurrentSeason()
    if (season.label === current.label) {
      setWeekStart(getWeekStart(new Date()))
    } else {
      setWeekStart(getWeekStart(new Date(season.end)))
    }
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-white">{sportLabel} Scores</h1>
        <div className="flex items-center gap-4">
          <FilterButtonGroup options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter} size="sm" />
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rankedOnly}
              onChange={e => setRankedOnly(e.target.checked)}
              className="rounded bg-pitch-800 border-pitch-700 text-accent focus:ring-accent/50"
            />
            Ranked only
          </label>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <WeekNavigator
          weekStart={weekStart}
          weekEnd={weekEnd}
          onPrev={handlePrevWeek}
          onNext={handleNextWeek}
          onToday={handleToday}
        />
        <SeasonSelector
          value={selectedSeason.label}
          options={SEASON_OPTIONS}
          onChange={handleSeasonChange}
        />
      </div>

      {/* Day filter: a button per date of the week; days with no games are grayed out. */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setSelectedDay(null)}
          className={clsx('px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
            selectedDay === null ? 'bg-accent text-white' : 'bg-pitch-800 text-slate-300 hover:bg-pitch-700')}
        >
          All week
        </button>
        {weekDays.map((d) => {
          const iso = toIsoDate(d)
          const count = dayCounts[iso] ?? 0
          const active = selectedDay === iso
          return (
            <button
              key={iso}
              disabled={count === 0}
              onClick={() => setSelectedDay(iso)}
              title={count === 0 ? 'No games' : `${count} game${count === 1 ? '' : 's'}`}
              className={clsx('px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                active ? 'bg-accent text-white'
                  : count === 0 ? 'bg-pitch-800/40 text-slate-600 cursor-not-allowed'
                    : 'bg-pitch-800 text-slate-300 hover:bg-pitch-700')}
            >
              {d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
            </button>
          )
        })}
      </div>

      {page.loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="card" className="h-44" />
          ))}
        </div>
      ) : page.data.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-slate-500">
            {selectedDay ? 'No games on this day.' : 'No games found for this week.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {page.data.map((game) => (
            <ScoreCard
              key={game.DT_RowId}
              game={{
                id: game.DT_RowId,
                starts_at: game.starts_at,
                status: game.status,
                period: game.period,
                clock: game.clock,
                home_team_name: game.home_team_name,
                away_team_name: game.away_team_name,
                home_team_slug: game.home_team_slug,
                away_team_slug: game.away_team_slug,
                home_score: game.home_score,
                away_score: game.away_score,
                venue: game.venue,
                home_team_conference: game.home_team_conference,
                home_team_logo_url: game.home_team_logo_url,
                away_team_logo_url: game.away_team_logo_url,
                home_team_rank: game.home_team_rank,
                away_team_rank: game.away_team_rank,
              }}
              sport={sport}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => fetchPage(page.start - page.length)}
            className="px-3 py-1.5 rounded-lg text-sm bg-pitch-800 text-slate-300 hover:bg-pitch-700 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-400">Page {currentPage} of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => fetchPage(page.start + page.length)}
            className="px-3 py-1.5 rounded-lg text-sm bg-pitch-800 text-slate-300 hover:bg-pitch-700 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
